import {
  SystemCapabilityProfile,
  IncidentRecord,
  AnalogueMatch,
  DimensionComparison,
  TransferableLesson,
  ConfidenceRating
} from '../data/collectSeaTypes';
import { INCIDENTS } from '../data/collectSeaIncidents';
import { MECHANISM_MAP } from '../data/collectSeaTaxonomy';

export const METHODOLOGY_VERSION = "2026.1";
export const SCORING_WEIGHTS_VERSION = "W-2026.1";

export interface MatcherOptions {
  maxResults?: number;
  minScoreThreshold?: number;
}

export function matchAnalogueIncidents(
  profile: SystemCapabilityProfile,
  options: MatcherOptions = {}
): AnalogueMatch[] {
  const maxResults = options.maxResults ?? 6;
  const minScore = options.minScoreThreshold ?? 0.05;

  const proposedCapIds = new Set(profile.capabilities.map(c => c.id));
  const proposedInputLower = profile.input_text.toLowerCase();

  const scoredMatches: AnalogueMatch[] = [];

  for (const incident of INCIDENTS) {
    const incCapIds = new Set(incident.collectsea_classification.capabilities);

    // 1. Capability Overlap (Overlap Coefficient)
    let overlapCount = 0;
    for (const capId of proposedCapIds) {
      if (incCapIds.has(capId)) overlapCount++;
    }
    const minSetSize = Math.min(proposedCapIds.size, incCapIds.size);
    const rawCapOverlap = minSetSize > 0 ? (overlapCount / minSetSize) : 0;
    const capabilityOverlap = Math.min(1.0, rawCapOverlap * 1.4);

    // 2. Consequence Overlap
    let consequenceScore = 0;
    const incConsequences = (incident.collectsea_classification.consequences || incident.collectsea_classification.failure_mechanisms || []).map(c => c.toLowerCase());
    for (const cons of (profile.consequence_characteristics || [])) {
      const cLower = cons.toLowerCase();
      if (incConsequences.some(ic => 
        (ic.includes('financial') && (cLower.includes('financial') || cLower.includes('money') || cLower.includes('refund') || cLower.includes('loss') || cLower.includes('wire') || cLower.includes('settlement') || cLower.includes('valuation') || cLower.includes('price'))) ||
        (ic.includes('loss') && cLower.includes('loss')) ||
        (ic.includes('tenant') && (cLower.includes('tenant') || cLower.includes('cross'))) ||
        (ic.includes('data') && cLower.includes('data')) ||
        (ic.includes('deletion') && (cLower.includes('deletion') || cLower.includes('destroy') || cLower.includes('cleanup') || cLower.includes('wipe'))) ||
        (ic.includes('compromise') && (cLower.includes('compromise') || cLower.includes('escape') || cLower.includes('privilege') || cLower.includes('secret') || cLower.includes('bypass') || cLower.includes('token') || cLower.includes('ssrf'))) ||
        (ic.includes('outage') && (cLower.includes('outage') || cLower.includes('disruption') || cLower.includes('routing') || cLower.includes('crash'))) ||
        (ic.includes('credential') && (cLower.includes('credential') || cLower.includes('secret') || cLower.includes('auth') || cLower.includes('token')))
      )) {
        consequenceScore += 0.65;
      }
    }
    consequenceScore = Math.min(1.0, consequenceScore);

    // 3. Action / Authority Similarity
    let rawAction = 0;
    if (proposedCapIds.has('auth.financial_action') && incCapIds.has('auth.financial_action')) rawAction += 0.85;
    if (proposedCapIds.has('auth.code_execution') && incCapIds.has('auth.code_execution')) rawAction += 0.85;
    if (proposedCapIds.has('auth.destructive_action') && incCapIds.has('auth.destructive_action')) rawAction += 0.85;
    if (proposedCapIds.has('auth.privileged_action') && incCapIds.has('auth.privileged_action')) rawAction += 0.70;
    if (proposedCapIds.has('auth.autonomous_approval') && incCapIds.has('auth.autonomous_approval')) rawAction += 0.60;
    if (proposedCapIds.has('auth.external_communication') && incCapIds.has('auth.external_communication')) rawAction += 0.50;
    const actionScore = Math.min(1.0, rawAction);

    // 4. Trust Boundary / Architectural Ingestion
    let rawTrust = 0;
    if (proposedCapIds.has('arch.multi_tenant') && incCapIds.has('arch.multi_tenant')) rawTrust += 0.80;
    if (proposedCapIds.has('data.credentials_secrets') && incCapIds.has('data.credentials_secrets')) rawTrust += 0.75;
    if (proposedCapIds.has('auth.infrastructure_modification') && incCapIds.has('auth.infrastructure_modification')) rawTrust += 0.60;
    if (proposedCapIds.has('arch.external_api') && incCapIds.has('arch.external_api')) rawTrust += 0.60;
    if (proposedCapIds.has('arch.tool_enabled_agent') && (incCapIds.has('arch.tool_enabled_agent') || incident.affected_system.is_agentic)) rawTrust += 0.50;
    const trustScore = Math.min(1.0, rawTrust);

    // 5. Semantic / Keyword Domain Alignment
    const incFullText = `${incident.title} ${incident.documented_facts.map(f => f.statement).join(' ')} ${incident.affected_system.product} ${incident.affected_system.organization} ${(incident.source_stated_contributors || []).join(' ')}`.toLowerCase();
    
    // Domain keywords boost
    const domainKeywords = ['refund', 'payment', 'redis', 'cache', 's3', 'bucket', 'agent', 'bot', 'pull request', 'container', 'log', 'telemetry', 'logger', 'proxy', 'trading', 'order', 'deprovisioning', 'vm', 'database', 'ssh', 'host key', 'oauth', 'token', 'wire', 'loan', 'idor', 'build', 'pipeline', 'disbursement', 'escrow', 'valuation', 'purchasing', 'hypervisor', 'ransomware', 'waf', 'ssrf'];
    let domainHits = 0;
    for (const kw of domainKeywords) {
      if (proposedInputLower.includes(kw) && incFullText.includes(kw)) {
        domainHits += 0.40;
      }
    }

    const queryWords = proposedInputLower.split(/\W+/).filter(w => w.length > 3);
    let wordMatches = 0;
    for (const w of queryWords) {
      if (incFullText.includes(w)) wordMatches++;
    }
    const rawWordScore = queryWords.length > 0 ? (wordMatches / queryWords.length) : 0;
    const semanticScore = Math.min(1.0, rawWordScore + domainHits);

    // 6. Contradiction Penalty
    let contradictionPenalty = 0;
    const isPropAi = (profile.actors || []).some(a => a.type === 'ai_agent');
    if (!isPropAi && incident.affected_system.is_ai_system) {
      contradictionPenalty += 0.05;
    }

    // Composite Score Calculation
    const compositeScore = (
      capabilityOverlap * 0.30 +
      actionScore * 0.25 +
      consequenceScore * 0.20 +
      trustScore * 0.15 +
      semanticScore * 0.10 -
      contradictionPenalty
    );

    const clampedScore = Math.max(0.05, Math.min(0.98, compositeScore));

    if (clampedScore >= minScore) {
      const matrix = buildAnalogyMatrix(profile, incident);
      const whyAnalogous = generateWhyAnalogous(profile, incident, matrix);
      const negativeEvidence = generateNegativeEvidence(profile, incident, matrix);
      const confidence = calculateConfidence(incident, clampedScore, matrix);

      const associatedMechanisms = incident.collectsea_classification.failure_mechanisms
        .map(id => MECHANISM_MAP.get(id))
        .filter((m): m is NonNullable<typeof m> => m !== undefined);

      const lessons = generateTransferableLessons(profile, incident, associatedMechanisms);

      scoredMatches.push({
        match_id: `MATCH-${incident.incident_id}`,
        incident,
        composite_score: Math.round(clampedScore * 100) / 100,
        breakdown: {
          capability_overlap_score: Math.round(capabilityOverlap * 100) / 100,
          consequence_overlap_score: Math.round(consequenceScore * 100) / 100,
          action_similarity_score: Math.round(actionScore * 100) / 100,
          trust_boundary_score: Math.round(trustScore * 100) / 100,
          semantic_similarity_score: Math.round(semanticScore * 100) / 100,
          contradiction_penalty: Math.round(contradictionPenalty * 100) / 100
        },
        analogy_matrix: matrix,
        why_analogous: whyAnalogous,
        negative_evidence_differences: negativeEvidence,
        confidence,
        associated_mechanisms: associatedMechanisms,
        lessons
      });
    }
  }

  // Sort descending by composite score
  scoredMatches.sort((a, b) => b.composite_score - a.composite_score);

  return scoredMatches.slice(0, maxResults);
}

function buildAnalogyMatrix(
  profile: SystemCapabilityProfile,
  incident: IncidentRecord
): DimensionComparison[] {
  const comparisons: DimensionComparison[] = [];
  const propCaps = new Set(profile.capabilities.map(c => c.id));
  const incCaps = new Set(incident.collectsea_classification.capabilities);

  // 1. Authority / Consequential Action
  const propFinancial = propCaps.has('auth.financial_action');
  const incFinancial = incCaps.has('auth.financial_action');
  const propDelete = propCaps.has('auth.destructive_action');
  const incDelete = incCaps.has('auth.destructive_action');
  const propCode = propCaps.has('auth.code_execution');
  const incCode = incCaps.has('auth.code_execution');

  if (propFinancial || incFinancial) {
    comparisons.push({
      dimension: "Financial Settlement / Consequential Mutation",
      category: "Authority",
      proposed_state: propFinancial ? "Automated Financial Mutation / Refund / Settlement" : "Non-Financial Logic",
      historical_state: incFinancial ? "Financial Transaction Engine / Ledger Mutation" : "Non-Financial State Mutation",
      match_status: propFinancial && incFinancial ? "MATCH" : "DIFFERENCE",
      explanation: propFinancial && incFinancial
        ? "Both systems initiate external financial transactions where duplicate execution or network timeouts carry direct monetary loss liability."
        : "Financial consequence profile differs."
    });
  }

  if (propDelete || incDelete) {
    comparisons.push({
      dimension: "Destructive Resource Deletion",
      category: "Authority",
      proposed_state: propDelete ? "Automated Resource Cleanup / File Deletion" : "Read-Only / Non-Destructive",
      historical_state: incDelete ? "Permanent Cloud / File Deletion Event" : "Non-Destructive Issue",
      match_status: propDelete && incDelete ? "MATCH" : "DIFFERENCE",
      explanation: propDelete && incDelete
        ? "Both architectures permit automated deletion operations that can lead to permanent data loss if authorization or input validation boundaries fail."
        : "Destructive deletion profile differs."
    });
  }

  if (propCode || incCode) {
    comparisons.push({
      dimension: "Dynamic Code / Command Execution",
      category: "Authority",
      proposed_state: propCode ? "Executes Dynamic Code / Terminal Shell / Tool Scripts" : "Pre-Compiled Static Code",
      historical_state: incCode ? "Dynamic Command / Sandbox Execution" : "Static Binary Execution",
      match_status: propCode && incCode ? "MATCH" : "DIFFERENCE",
      explanation: propCode && incCode
        ? "Both systems evaluate dynamically generated or user-supplied instructions, creating container escape, template injection, and privilege escalation risks."
        : "Dynamic execution profile differs."
    });
  }

  // 2. Data Boundary & Multi-Tenancy
  const propTenant = propCaps.has('arch.multi_tenant') || propCaps.has('data.cross_tenant_data');
  const incTenant = incCaps.has('arch.multi_tenant') || incCaps.has('data.cross_tenant_data');
  if (propTenant || incTenant) {
    comparisons.push({
      dimension: "Multi-Tenant Data Isolation",
      category: "Data",
      proposed_state: propTenant ? "Multi-Tenant SaaS / Shared Caching Infrastructure" : "Single-Tenant Dedicated",
      historical_state: incTenant ? "Multi-Tenant Shared Architecture" : "Single Tenant Environment",
      match_status: propTenant && incTenant ? "MATCH" : "DIFFERENCE",
      explanation: propTenant && incTenant
        ? "Both architectures service multiple distinct organizations on shared storage or caching infrastructure, vulnerable to cross-tenant data leakage if isolation fails."
        : "Tenant isolation scope differs."
    });
  }

  // 3. AI Agent Autonomy
  const isPropAi = (profile.actors || []).some(a => a.type === 'ai_agent') || propCaps.has('arch.tool_enabled_agent');
  const isIncAi = incident.affected_system.is_ai_system || incident.affected_system.is_agentic || incCaps.has('arch.tool_enabled_agent');
  comparisons.push({
    dimension: "Autonomous Decision Authority",
    category: "Architecture",
    proposed_state: isPropAi ? "Autonomous System / AI Initiates Actions" : "Deterministic Human / Cron Initiation",
    historical_state: isIncAi ? "Automated Control Plane / Agentic Router" : "Conventional Cloud Infrastructure",
    match_status: isPropAi === isIncAi ? "MATCH" : "PARTIAL",
    explanation: isPropAi && isIncAi
      ? "Both systems delegate consequential decisions to automated algorithms or agents without continuous synchronous human oversight."
      : "Degree of autonomous decision authority differs, though underlying failure mechanics remain transferable."
  });

  return comparisons;
}

function generateWhyAnalogous(
  profile: SystemCapabilityProfile,
  incident: IncidentRecord,
  matrix: DimensionComparison[]
): string {
  const incProd = incident.affected_system.product;
  const incCaps = new Set(incident.collectsea_classification.capabilities);
  const propCaps = new Set(profile.capabilities.map(c => c.id));

  // Context-specific plain-English engineering explanations (100% anonymized, zero brand names)
  if (propCaps.has('auth.financial_action') && incCaps.has('auth.financial_action')) {
    if (incident.incident_id === 'SEA-POST-001' || incident.incident_id === 'SEA-POST-002') {
      return `Why this relates to your proposal: You are building a system that executes financial state mutations (e.g. payments/refunds). In this case study, network timeouts caused client retry requests to execute duplicate transactions. This architecture reference demonstrates why idempotency keys and transactional reservation locks are mandatory to make external mutation calls safe.`;
    }
    if (incident.incident_id === 'SEA-SEC-011' || incident.incident_id === 'SEA-REG-003') {
      return `Why this relates to your proposal: You are building an automated or algorithmic decision workflow that allocates value or executes transactions. In this regulatory disclosure, automated algorithmic models produced unintended financial execution errors when inputs drifted, demonstrating why deterministic bounds and safety limits are required on automated mutations.`;
    }
    if (incident.incident_id === 'SEA-REG-001') {
      return `Why this relates to your proposal: You are building an automated disbursement workflow. In this financial wire case, ambiguous interface controls in automated payment software caused an unintended multi-million dollar principal disbursement, illustrating why high-value automated transactions require plain-language confirmation gates.`;
    }
  }

  if (propCaps.has('data.credentials_secrets') && incCaps.has('data.credentials_secrets')) {
    if (incident.incident_id === 'SEA-SEC-001') {
      return `Why this relates to your proposal: Your architecture manages authentication tokens and application permissions. In this public regulatory disclosure, attackers used compromised legacy credentials to generate over-privileged OAuth apps and access executive communications, demonstrating why all service accounts need mandatory MFA and token pruning.`;
    }
    if (incident.incident_id === 'SEA-SEC-006') {
      return `Why this relates to your proposal: Your system ingests user-uploaded files or diagnostic logs. In this regulatory case study, support troubleshooting archives (HAR files) contained unredacted session cookies that allowed attackers to hijack active admin sessions, proving why pre-ingestion credential redaction is mandatory.`;
    }
  }

  if (propCaps.has('auth.destructive_action') && incCaps.has('auth.destructive_action')) {
    return `Why this relates to your proposal: Your workflow includes automated resource cleanup or deletion routines. In this cloud infrastructure case study, an automated background worker executed unvalidated teardown commands without multi-stage human approval or soft-delete grace periods, accidentally purging an entire enterprise tenant.`;
  }

  if (propCaps.has('arch.multi_tenant') && incCaps.has('arch.multi_tenant')) {
    if (incident.incident_id === 'SEA-POST-003') {
      return `Why this relates to your proposal: Your proposed system isolates data across multiple organizations on shared infrastructure. In this architecture postmortem, cache socket connection pooling race conditions caused data from one tenant to leak to another, proving why tenant isolation must be enforced at the storage/RLS layer.`;
    }
    if (incident.incident_id === 'SEA-SEC-009') {
      return `Why this relates to your proposal: Your system serves documents or resources via URLs. In this regulatory enforcement order, sequential numeric URLs without session authorization allowed public access to confidential records (IDOR), showing why resource keys require cryptographic UUIDs and strict session validation.`;
    }
  }

  if (propCaps.has('auth.code_execution') && incCaps.has('auth.code_execution')) {
    if (incident.incident_id === 'SEA-SEC-010') {
      return `Why this relates to your proposal: Your system compiles or executes automated build scripts. In this build security case, pipeline servers lacked deterministic binary verification, allowing unauthorized compilation hooks to compromise downstream software releases.`;
    }
    return `Why this relates to your proposal: Your system evaluates dynamic scripts or user instructions. In this incident (${incProd}), untrusted code broke out of container boundaries, proving why dynamic executions require hypervisor-isolated microVM sandboxing.`;
  }

  const matchingDimensions = matrix.filter(m => m.match_status === "MATCH").map(m => m.dimension);
  if (matchingDimensions.length > 0) {
    return `Why this relates to your proposal: Both systems share critical architectural patterns across ${matchingDimensions.slice(0, 2).join(' and ')}, presenting analogous failure modes that require proactive defensive boundaries.`;
  }

  return `Why this relates to your proposal: This documented failure in ${incProd} exhibits structural failure mechanisms in external API dependencies and boundary authorization relevant to your proposed workflow.`;
}

function generateNegativeEvidence(
  profile: SystemCapabilityProfile,
  incident: IncidentRecord,
  matrix: DimensionComparison[]
): string {
  const diffDimensions = matrix.filter(m => m.match_status === "DIFFERENCE" || m.match_status === "PARTIAL");
  if (diffDimensions.length > 0) {
    return `Critical distinction to keep in mind: While the underlying failure mechanics are analogous, notice the difference in ${diffDimensions[0].dimension}: ${diffDimensions[0].explanation}`;
  }

  return "No major architectural contradictions identified. The defensive lessons and failure safeguards transfer directly to your proposed design.";
}

function calculateConfidence(
  incident: IncidentRecord,
  score: number,
  matrix: DimensionComparison[]
): ConfidenceRating {
  const matchesCount = matrix.filter(m => m.match_status === "MATCH").length;
  
  let sourceRating: "HIGH" | "MEDIUM" | "LOW" = "HIGH";
  if ((incident.source_type as string) === "unverified_report") sourceRating = "LOW";
  else if (incident.source_type === "academic_research") sourceRating = "MEDIUM";

  const causalRating: "HIGH" | "MEDIUM" | "LOW" = incident.documented_facts.length >= 2 ? "HIGH" : "MEDIUM";
  const analogyRating: "HIGH" | "MEDIUM" | "LOW" = score >= 0.45 ? "HIGH" : (score >= 0.25 ? "MEDIUM" : "LOW");
  const transferRating: "HIGH" | "MEDIUM" | "TENTATIVE" = matchesCount >= 2 ? "HIGH" : "MEDIUM";

  return {
    source_evidence: sourceRating,
    causal_support: causalRating,
    analogy_relevance: analogyRating,
    transferability: transferRating,
    overall_score: score
  };
}

function generateTransferableLessons(
  profile: SystemCapabilityProfile,
  incident: IncidentRecord,
  mechanisms: any[]
): TransferableLesson[] {
  const lessons: TransferableLesson[] = [];

  for (const mech of mechanisms) {
    if (mech.failure_mechanism_id === 'SEA-FM-001') {
      lessons.push({
        lesson_id: `LES-${incident.incident_id}-001`,
        failure_mechanism_id: mech.failure_mechanism_id,
        title: "Enforce Client-Generated Idempotency Keys on All Mutating Endpoints",
        statement: "When calling external APIs that execute financial or stateful mutations, never treat network timeouts as proofs of failure. Generate stable client-side idempotency keys and store transaction state to safely retry without duplicate side-effects.",
        supporting_incident_ids: [incident.incident_id],
        evidence_strength: "STRONG",
        transferability_status: "DIRECTLY_TRANSFERS",
        applicability_note: "Directly applicable to your proposed system's external mutation calls."
      });
    } else if (mech.failure_mechanism_id === 'SEA-FM-002') {
      lessons.push({
        lesson_id: `LES-${incident.incident_id}-002`,
        failure_mechanism_id: mech.failure_mechanism_id,
        title: "Constrain AI Customer Communications with Deterministic Rule Engines",
        statement: "Do not rely on unconstrained generative language models for policy, tariff, or refund commitments. Gated verification against a structured rule engine must validate promises before output delivery.",
        supporting_incident_ids: [incident.incident_id],
        evidence_strength: "STRONG",
        transferability_status: "DIRECTLY_TRANSFERS",
        applicability_note: "Directly prevents legal and financial liability from automated conversational assertions."
      });
    } else if (mech.failure_mechanism_id === 'SEA-FM-003') {
      lessons.push({
        lesson_id: `LES-${incident.incident_id}-003`,
        failure_mechanism_id: mech.failure_mechanism_id,
        title: "Isolate Tenant Context in Cache Keys and Enforce Storage-Layer RLS",
        statement: "Never rely solely on application-layer filtering for multi-tenant data. Always namespace cache keys with cryptographic tenant identifiers and enforce Row-Level Security (RLS) at the database layer.",
        supporting_incident_ids: [incident.incident_id],
        evidence_strength: "STRONG",
        transferability_status: "DIRECTLY_TRANSFERS",
        applicability_note: "Directly protects multi-tenant shared data pools from cross-tenant leakage."
      });
    } else if (mech.failure_mechanism_id === 'SEA-FM-004') {
      lessons.push({
        lesson_id: `LES-${incident.incident_id}-004`,
        failure_mechanism_id: mech.failure_mechanism_id,
        title: "Enforce Hardware MFA and Ephemeral Sandboxing on Privileged Operations",
        statement: "Enforce hardware-backed multi-factor authentication on all service principal accounts and isolate dynamic script execution inside ephemeral microVM sandboxes.",
        supporting_incident_ids: [incident.incident_id],
        evidence_strength: "STRONG",
        transferability_status: "DIRECTLY_TRANSFERS",
        applicability_note: "Directly mitigates credential spraying and execution boundary escape."
      });
    } else if (mech.failure_mechanism_id === 'SEA-FM-005') {
      lessons.push({
        lesson_id: `LES-${incident.incident_id}-005`,
        failure_mechanism_id: mech.failure_mechanism_id,
        title: "Mandate Multi-Stage Human Approval & Soft-Delete Grace Periods for Teardowns",
        statement: "Automated cleanup scripts and deprovisioning workers must never immediately purge production resources. Implement mandatory human verification gates and soft-delete grace periods.",
        supporting_incident_ids: [incident.incident_id],
        evidence_strength: "STRONG",
        transferability_status: "DIRECTLY_TRANSFERS",
        applicability_note: "Directly prevents irreversible production data wipeout from automation defects."
      });
    } else if (mech.failure_mechanism_id === 'SEA-FM-006') {
      lessons.push({
        lesson_id: `LES-${incident.incident_id}-006`,
        failure_mechanism_id: mech.failure_mechanism_id,
        title: "Deploy Circuit Breakers and Staged Canary Rollouts for Dynamic Systems",
        statement: "Enforce hard rate-limiting circuit breakers and canary staged rollouts for dynamic configurations and automated loops to prevent cascading global outages.",
        supporting_incident_ids: [incident.incident_id],
        evidence_strength: "STRONG",
        transferability_status: "DIRECTLY_TRANSFERS",
        applicability_note: "Directly protects against runaway execution loops and edge failover storms."
      });
    }
  }

  if (lessons.length === 0) {
    lessons.push({
      lesson_id: `LES-${incident.incident_id}-GEN`,
      failure_mechanism_id: incident.collectsea_classification.failure_mechanisms[0] || 'SEA-FM-004',
      title: "Establish Strict Pre-Execution Boundary Safeguards",
      statement: "Validate authority, rate limits, and idempotency identifiers prior to initiating external state mutations.",
      supporting_incident_ids: [incident.incident_id],
      evidence_strength: "MODERATE",
      transferability_status: "TRANSFERS_WITH_CONDITIONS",
      applicability_note: "Provides general defensive boundary guidelines for proposed architecture."
    });
  }

  return lessons;
}
